<template>
    <div class="position-relative container px-0 mt-1">
        <div class="d-flex justify-content-between align-items-center mb-0 mb-xl-2">
            <h3 v-if="currencyCode"
                class="mb-2 mb-lg-0 h3-small">
                <translate translation-key="socials.socialSignalsAndTrafficTrackerWithNameAndSymbol"
                           :replacements="{ name: name, currency_code: currencyCode }" />
            </h3>
            <h3 v-else
                class="mb-2 mb-lg-0 h3-small">
                <translate translation-key="socials.socialSignalsAndTrafficTrackerWithName"
                           :replacements="{ name: name}" />
            </h3>
            <div class="d-flex">
                <div id="chart-expand"
                     class="d-none d-md-flex justify-content-center align-items-center chart-expand"
                     @click="toggleFs">
                    <i class="fa-solid fa-expand" />
                </div>
                <div v-if="!hideExport"
                     :id="`export-menu-tippy-socials-export`"
                     class="export-menu"
                     role="tooltip"
                     aria-label="Export menu">
                    <i class="fa-solid fa-share" />
                </div>
            </div>
        </div>
        <div class="d-flex selectors justify-content-between align-items-center flex-wrap">
            <div class="d-flex tabs flex-column align-items-start">
                <div class="d-flex align-items-center view-selector"
                     :class="`tabs-count-${Object.keys(enabledSocials).length}`">
                    <button v-for="(tab, index) in tabs"
                            :key="tab.title"
                            class="d-flex btn"
                            :class="{active: (index === selectedTabIndex)}"
                            @click="selectTab(index)">
                        <span>
                            {{ tab.title }}
                        </span>
                    </button>
                </div>
                <div v-if="selectedSocial"
                     class="mt-2 mb-1 visit-social">
                    <translate translation-key="socials.visit" /> <a :href="selectedSocial.link"
                                                                     rel="noopener nofollow noindex"
                                                                     target="_blank">
                        {{ selectedSocial.name }}
                    </a>
                </div>
            </div>
            <div class="range-selector d-flex flex-row align-items-center">
                <button v-for="(item, index) in Object.keys(rangeSelector)"
                        :key="index"
                        class="btn d-flex"
                        :class="{active: (index === selectedRangeIndex)}"
                        @click="selectRange(index)">
                    <span class="text-uppercase">
                        {{ rangeSelector[item] }}
                    </span>
                </button>
                <button class="position-relative d-flex btn"
                        :class="{active: isLogarithmic}"
                        @click="toggleLogAxis">
                    <span class="log-view">LOG</span>
                </button>
            </div>
        </div>
        <highcharts ref="socialsChart"
                    class="highcharts-socials-chart"
                    :constructor-type="'stockChart'"
                    :options="chartOptions" />
        <div id="socials-export">
            <ul>
                <li id="export-png"
                    @click="exportImage('socials', 'image/png')">
                    <translate translation-key="charts.downloadPngImage" />
                </li>
                <li id="export-jpeg"
                    @click="exportImage('socials', 'image/jpeg')">
                    <translate translation-key="charts.downloadJpegImage" />
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    import { Chart } from 'highcharts-vue';
    import axios from 'axios';
    import Highcharts from 'highcharts';
    import stockInit from 'highcharts/modules/stock';
    import exportingInit from 'highcharts/modules/exporting';
    import { tippy } from 'vue-tippy';
    import { addChartWatermark } from '../helpers/helpers';
    import { getTrans } from '../helpers/getTrans';
    import { CDN_CHARTS_LOADER } from '../constants/assets';

    stockInit(Highcharts);
    exportingInit(Highcharts);

    export default {
        name: 'SocialsChart',
        components: { highcharts: Chart },
        props: {
            name: {
                type: String,
                required: true,
                default: '',
            },
            currencyCode: {
                type: String,
                required: false,
                default: '',
            },
            slug: {
                type: String,
                required: false,
                default: '',
            },
            enabledSocials: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                selectedSocial: null,
                isFirstLoad: true,
                hideExport: false,
                hcInstance: null,
                range: null,
                selectedTabIndex: null,
                selectedRangeIndex: null,
                tabs: [],
                rangeSelector: {
                    '1m': getTrans('time.oneMonth', '1m'),
                    '3m': getTrans('time.threeMonths', '3m'),
                    '6m': getTrans('time.sixMonths', '6m'),
                    '1y': getTrans('time.oneYear', '1y'),
                    ytd: getTrans('time.ytd', 'ytd'),
                    all: getTrans('charts.all'),
                },
                isLogarithmic: false,
                chartOptions: {
                    chart: {
                        height: 485,
                        zoomType: 'x',
                        animation: {
                            duration: 1000,
                        },
                        marginRight: 0,
                        marginBottom: 10,
                        events: {
                            load() {
                                const initialWidth = this.chartWidth - 120;
                                const initialHeight = this.chartHeight * 0.65 - 34;
                                const fullscreenHeight = window.innerHeight - 180;
                                const fullscreenWidth = window.innerWidth - 130;

                                this.renderer.image('https://assets.bitdegree.org/crypto-prices/assets/bitdegree-watermark.png', initialWidth, initialHeight, 120, 34).add().addClass('not-fullscreen');
                                this.renderer.image('https://assets.bitdegree.org/crypto-prices/assets/bitdegree-watermark.png', fullscreenWidth, fullscreenHeight, 120, 34).add().addClass('fullscreen');
                            },
                        },
                    },
                    accessibility: {
                        enabled: false,
                    },
                    credits: {
                        enabled: false,
                    },
                    time: {
                        useUTC: true,
                    },
                    rangeSelector: {
                        enabled: false,
                    },
                    animation: true,
                    xAxis: {
                        crosshair: {
                            label: {
                                type: 'datetime',
                                enabled: true,
                                format: '{value:%m/%d/%Y %I:%M %p}',
                                backgroundColor: '#666666',
                            },
                        },
                        type: 'datetime',
                        dateTimeLabelFormats: {
                            day: '%e',
                            hour: '%I:%M %p',
                        },
                    },
                    tooltip: {
                        followTouchMove: true,
                        followPointer: true,
                        borderColor: 'none',
                        backgroundColor: 'white',
                        borderRadius: '8',
                        shadow: false,
                        shared: true,
                        split: false,
                        useHTML: true,
                        positioner(labelWidth, labelHeight, point) {
                            let tooltipX = (point.plotX) + this.chart.plotLeft + 20;
                            let tooltipY = (point.plotY - 60) - this.chart.plotTop + labelHeight;

                            if (point.plotX + labelWidth + 20 > this.chart.plotWidth) {
                                tooltipX = (point.plotX) + this.chart.plotLeft - labelWidth - 20;
                            }

                            if ((point.plotY - labelHeight - this.chart.plotTop) > this.chart.plotTop) {
                                tooltipY = (point.plotY - 100) + this.chart.plotTop - 20;
                            }

                            return {
                                x: tooltipX,
                                y: tooltipY,
                            };
                        },
                        formatter() {
                            const date = Highcharts.dateFormat('%m/%d/%Y', this.points[0].x);
                            const first = (this.points[0] !== undefined ? this.points[0].y : 0).toLocaleString('en-US');

                            if (this.points[2]) {
                                const second = (this.points[1] !== undefined ? this.points[1].y : 0).toLocaleString('en-US');
                                const third = (this.points[2] !== undefined ? this.points[2].y : 0).toLocaleString('en-US');

                                return '<div class="floating-tooltip p-2">'
                                    + '<div class="d-flex align-items-center mb-2">'
                                    + `<span class="primary">${date}</span>`
                                    + '</div>'
                                    + '<div class="d-flex align-items-center">'
                                    + `<span style="color: ${this.points[0].color}" class="indicator">\u25CF</span>`
                                    + `<span class="sub">${this.points[0].series.name}: &nbsp</span>`
                                    + `<span class="number">${first}</span>`
                                    + '</div>'
                                    + '<div class="d-flex align-items-center">'
                                    + '<span style="color: #9884fc" class="indicator">\u25CF</span>'
                                    + `<span class="sub">${this.points[1].series.name}: &nbsp</span>`
                                    + `<span class="number">${second}</span>`
                                    + '</div>'
                                    + '<div class="d-flex align-items-center">'
                                    + '<span style="color: #ffcd00" class="indicator">\u25CF</span>'
                                    + `<span class="sub">${this.points[2].series.name}: &nbsp</span>`
                                    + `<span class="number">${third}</span>`
                                    + '</div>'
                                    + '</div>';
                            }

                            if (this.points[1]) {
                                const second = (this.points[1] !== undefined ? this.points[1].y : 0).toLocaleString('en-US');

                                return '<div class="floating-tooltip p-2">'
                                    + '<div class="d-flex align-items-center mb-2">'
                                    + `<span class="primary">${date}</span>`
                                    + '</div>'
                                    + '<div class="d-flex align-items-center">'
                                    + `<span style="color: ${this.points[0].color}" class="indicator">\u25CF</span>`
                                    + `<span class="sub">${this.points[0].series.name}: &nbsp</span>`
                                    + `<span class="number">${first}</span>`
                                    + '</div>'
                                    + '<div class="d-flex align-items-center">'
                                    + '<span style="color: #9884fc" class="indicator">\u25CF</span>'
                                    + `<span class="sub">${this.points[1].series.name}: &nbsp</span>`
                                    + `<span class="number">${second}</span>`
                                    + '</div>'
                                    + '</div>';
                            }

                            return '<div class="floating-tooltip p-2">'
                                + '<div class="d-flex align-items-center mb-2">'
                                + `<span class="primary">${date}</span>`
                                + '</div>'
                                + '<div class="d-flex align-items-center">'
                                + `<span style="color: ${this.points[0].color}" class="indicator">\u25CF</span>`
                                + `<span class="sub">${this.points[0].series.name}: &nbsp</span>`
                                + `<span class="number">${first}</span>`
                                + '</div>'
                                + '</div>';
                        },
                    },
                    yAxis: [
                        {
                            opposite: false,
                            labels: {
                                enabled: false,
                            },
                            crosshair: {
                                dashStyle: 'dot',
                                width: 1,
                                color: 'grey',
                                snap: false,
                                label: {
                                    enabled: false,
                                    useHTML: true,
                                },
                            },
                            lineColor: '#e6e6e6',
                            lineWidth: 1,
                        },
                        {
                            opposite: false,
                            labels: {
                                enabled: false,
                            },
                            lineColor: '#e6e6e6',
                            lineWidth: 1,
                        },
                        {
                            labels: {
                                enabled: false,
                            },
                            opposite: false,
                        },
                    ],
                    navigation: {
                        menuItemHoverStyle: {
                            background: '#f8fafd',
                            color: '#000000',
                        },
                        contextMenuStyle: {
                            border: 'none',
                        },
                        buttonOptions: {
                            height: 25,
                            y: 0,
                        },
                    },
                    navigator: {
                        enabled: false,
                    },
                    responsive: {
                        rules: [{
                            chartOptions: {
                                chart: {
                                    marginRight: 0,
                                },
                                tooltip: {
                                    followPointer: false,
                                    positioner(labelWidth, labelHeight, point) {
                                        const tooltipX = this.chart.plotLeft;
                                        let tooltipY = (point.plotY) - this.chart.plotTop;

                                        if ((point.plotY - labelHeight - this.chart.plotTop) > this.chart.plotTop) {
                                            tooltipY = (point.plotY - 100) + this.chart.plotTop - 20;
                                        }

                                        return {
                                            x: tooltipX,
                                            y: tooltipY,
                                        };
                                    },
                                },
                            },
                            condition: {
                                maxWidth: 540,
                            },
                        }],
                    },
                    exporting: {
                        enabled: false,
                    },
                    legend: {
                        enabled: true,
                        layout: 'horizontal',
                        align: 'center',
                        verticalAlign: 'bottom',
                        x: 0,
                        y: -35,
                        floating: true,
                    },
                    series: [
                        {
                            type: 'line',
                            data: [],
                            yAxis: 0,
                            threshold: null,
                            lineWidth: 3,
                            dataGrouping: {
                                enabled: false,
                            },
                            color: '#17c783',
                            states: {
                                inactive: {
                                    opacity: 1,
                                },
                                hover: {
                                    halo: null,
                                    lineWidthPlus: 0,
                                },
                            },
                        },
                        {
                            type: 'line',
                            data: [],
                            yAxis: 1,
                            threshold: null,
                            lineWidth: 3,
                            dataGrouping: {
                                enabled: false,
                            },
                            lineColor: '#9884fc',
                            color: '#9884fc',
                            fillColor: {
                                linearGradient: {
                                    x1: 0, y1: 0, x2: 0, y2: 1,
                                },
                                stops: [
                                    [0, 'rgba(152,132,252, 0.4)'],
                                    [1, 'rgba(152,132,252, 0)'],
                                ],
                            },
                            states: {
                                inactive: {
                                    opacity: 1,
                                },
                                hover: {
                                    halo: null,
                                    lineWidthPlus: 0,
                                },
                            },
                        },
                        {
                            type: 'line',
                            data: [],
                            yAxis: 2,
                            threshold: null,
                            lineWidth: 3,
                            dataGrouping: {
                                enabled: false,
                            },
                            lineColor: '#ffcd00',
                            color: '#ffcd00',
                            states: {
                                inactive: {
                                    opacity: 1,
                                },
                                hover: {
                                    halo: null,
                                    lineWidthPlus: 0,
                                },
                            },
                        },
                    ],
                },
            };
        },
        mounted() {
            Highcharts.removeEvent(Highcharts.Chart, 'beforeShowResetZoom');
            this.hcInstance = this.$refs.socialsChart;
            this.addChartWatermark(this.hcInstance.chart);
            Object.keys(this.enabledSocials).forEach((social, index) => {
                if (social === 'github') {
                    this.tabs[index] = {
                        title: social.charAt(0).toUpperCase() + social.slice(1, 3) + social.charAt(3).toUpperCase() + social.slice(4),
                    };
                } else if (social === 'twitter') {
                    this.tabs[index] = {
                        title: 'X',
                    };
                } else if (social === 'domain') {
                    this.tabs[index] = {
                        title: getTrans('socials.website'),
                    };
                } else {
                    this.tabs[index] = {
                        title: social.charAt(0).toUpperCase() + social.slice(1),
                    };
                }
            });
            this.selectedSocial = Object.values(this.enabledSocials)[0];
            this.selectTab(0, false);
            this.selectRange(5);
            this.createTippy('socials-export');
        },
        methods: {
            changeRange(selectedRange, slug) {
                this.hcInstance.chart.showLoading(`<img class="highchart-loader" src="${CDN_CHARTS_LOADER}"/>`);
                const type = this.currencyCode ? 'cryptocurrencies' : 'exchanges';
                axios.get(`/api/${type}/social-chart/${slug}?period=${selectedRange}&social=${Object.keys(this.enabledSocials)[this.selectedTabIndex]}`)
                    .then((res) => {
                        this.chartOptions.series[0].data = res.data[0].data;
                        this.chartOptions.series[0].name = getTrans(`socials.${res.data[0].name}`);

                        this.chartOptions.yAxis[1].visible = false;
                        this.chartOptions.series[1].showInLegend = false;
                        this.chartOptions.series[1].visible = false;
                        this.chartOptions.yAxis[1].reversed = false;

                        this.chartOptions.yAxis[2].visible = false;
                        this.chartOptions.series[2].showInLegend = false;
                        this.chartOptions.series[2].visible = false;

                        if (res.data[1]) {
                            this.chartOptions.series[1].data = res.data[1].data;
                            this.chartOptions.series[1].name = getTrans(`socials.${res.data[1].name}`);
                            this.chartOptions.yAxis[1].visible = true;
                            this.chartOptions.series[1].showInLegend = true;
                            this.chartOptions.series[1].visible = true;
                        }

                        if (Object.keys(this.enabledSocials)[this.selectedTabIndex] === 'domain') {
                            this.chartOptions.yAxis[1].reversed = true;
                        }

                        if (res.data[2]) {
                            this.chartOptions.series[2].data = res.data[2].data;
                            this.chartOptions.series[2].name = getTrans(`socials.${res.data[2].name}`);
                            this.chartOptions.yAxis[2].visible = true;
                            this.chartOptions.series[2].showInLegend = true;
                            this.chartOptions.series[2].visible = true;
                        }
                    })
                    .finally(() => {
                        this.range = selectedRange;
                        this.hcInstance.chart.hideLoading();
                    });
            },
            selectTab(i, changeRange = true) {
                if (this.selectedTabIndex !== i) {
                    this.selectedTabIndex = i;
                    this.selectRange(this.selectedRangeIndex, changeRange);
                    this.tabs.forEach((tab, index) => {
                        tab.isActive = (index === i);
                    });
                    if (this.isFirstLoad === true) {
                        this.isFirstLoad = false;

                        return;
                    }
                    this.selectedSocial = Object.values(this.enabledSocials)[this.selectedTabIndex];
                    this.changeRange(Object.keys(this.rangeSelector)[this.selectedRangeIndex], this.slug);
                }
            },
            selectRange(i) {
                if (this.selectedRangeIndex !== i) {
                    this.selectedRangeIndex = i;
                    this.changeRange(Object.keys(this.rangeSelector)[this.selectedRangeIndex], this.slug);
                }

                this.hideExport = this.selectedRangeIndex === 5;
            },
            chartExportOptions() {
                return {
                    chart: {
                        events: {
                            load() {
                                this.renderer.image('https://assets.bitdegree.org/crypto-prices/assets/bitdegree-watermark.png', 1070, 410, 120, 34).add();
                            },
                        },
                    },
                    navigator: {
                        enabled: false,
                    },
                    scrollbar: {
                        enabled: false,
                    },
                };
            },
            exportImage(name, type) {
                const filename = `${this.tabs[this.selectedTabIndex].title}_${this.name}_${this.range.toUpperCase()}_chart`;
                this.hcInstance.chart.exportChart({
                    type,
                    filename,
                    sourceWidth: 1200,
                    sourceHeight: 600,
                }, this.chartExportOptions());
            },
            toggleFs() {
                this.hcInstance.chart.fullscreen.toggle();
            },
            toggleLogAxis() {
                this.toggleLogAxes(this.isLogarithmic);
                this.isLogarithmic = !this.isLogarithmic;
            },
            toggleLogAxes(isLogarithmic) {
                this.hcInstance.chart.update({
                    yAxis: [
                        {
                            type: isLogarithmic ? 'liner' : 'logarithmic',
                        },
                        {
                            type: isLogarithmic ? 'liner' : 'logarithmic',
                        },
                        {
                            type: isLogarithmic ? 'liner' : 'logarithmic',
                        },
                    ],
                });
            },
            createTippy(id) {
                const element = document.getElementById(id);
                if (element) {
                    element.style.display = 'block';

                    tippy(`#export-menu-tippy-${id}`, {
                        theme: 'tip-dropdown',
                        content: element,
                        placement: 'bottom',
                        animation: 'fade',
                        interactive: true,
                        trigger: 'click',
                        aria: null,
                    });
                }
            },
            addChartWatermark,
        },
    };
</script>
